// =============================================================================
// FILE: packages/renderer/src/components/capture/R2CaptureLauncher.tsx
// PURPOSE: R2 Capture Mode UI — floating launcher + collect → review → approve
//   → ingest → QA/QC panel.
//   KEY REDESIGN: when stage === 'collecting', the full panel collapses into a
//   small translucent floating pill so it never blocks the workspace.
// =============================================================================

import { useMemo, useState, useRef, useCallback, useEffect, type JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus, X, Type, Link2, Mail, StickyNote, FileText, Image, Camera, Paperclip,
  CheckCircle2, AlertTriangle, ClipboardCheck, Sparkles, ArrowRight, Trash2,
  Mic, Pause, ChevronUp, ChevronDown,
} from 'lucide-react';
import { useCapture, CAPTURE_TYPES, type CaptureType } from '../../stores/capture.store.js';
import { useUndoEngine } from '../../stores/undoEngine.store.js';

const ICONS: Record<CaptureType, JSX.Element> = {
  text: <Type className="h-3.5 w-3.5" />, link: <Link2 className="h-3.5 w-3.5" />, email: <Mail className="h-3.5 w-3.5" />,
  note: <StickyNote className="h-3.5 w-3.5" />, document: <FileText className="h-3.5 w-3.5" />, image: <Image className="h-3.5 w-3.5" />,
  screenshot: <Camera className="h-3.5 w-3.5" />, attachment: <Paperclip className="h-3.5 w-3.5" />,
};

export function R2CaptureLauncher(): JSX.Element {
  const c = useCapture();

  // When collecting: show the floating pill instead of the full panel
  if (c.open && c.stage === 'collecting') {
    return createPortal(<CapturePill />, document.body);
  }

  return (
    <>
      {/* Floating launcher — positioned above the R2 Command Centre panel */}
      {!c.open && (
        <button type="button" data-testid="r2-capture-launcher" data-nosound onClick={c.openPanel}
          title="R2 Capture™ — capture project intelligence for automated BIM and ICT workflows"
          style={{
            position: 'fixed', right: 20, bottom: 240, zIndex: 9550,
            display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
            padding: '10px 16px', borderRadius: 12, cursor: 'pointer',
            background: 'linear-gradient(135deg, rgba(45,212,191,0.92), rgba(13,148,136,0.92))',
            color: '#04121b', border: '1px solid rgba(94,234,212,0.6)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4), 0 0 24px rgba(45,212,191,0.35)',
            minWidth: 160,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%' }}>
            <Plus className="h-4 w-4" strokeWidth={2.5} />
            <span style={{ fontWeight: 800, fontSize: 13, letterSpacing: '0.02em' }}>
              R2 Capture™
            </span>
            {c.items.length > 0 && (
              <span style={{ background: '#04121b', color: '#5eead4', borderRadius: 999, fontSize: 10, padding: '1px 6px', marginLeft: 'auto' }}>
                {c.items.length}
              </span>
            )}
          </div>
          <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.65, letterSpacing: '0.05em', paddingLeft: 23 }}>
            Capture · Analyze · Document
          </span>
        </button>
      )}
      {c.open && createPortal(<CapturePanel />, document.body)}
    </>
  );
}

// ── Floating Translucent Pill (collecting mode) ───────────────────────────────
function CapturePill(): JSX.Element {
  const c      = useCapture();
  const push   = useUndoEngine(s => s.push);
  const [expanded, setExpanded] = useState(false);
  const [elapsed, setElapsed]   = useState(0);
  const [hovered, setHovered]   = useState(false);
  const [pos, setPos]           = useState({ x: 20, y: 240 }); // right, bottom — above R2 Command Centre
  const dragging                = useRef(false);
  const dragOffset              = useRef({ x: 0, y: 0 });
  const pillRef                 = useRef<HTMLDivElement>(null);

  // Elapsed timer
  useEffect(() => {
    const iv = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Drag
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = pillRef.current;
    if (!el) return;
    dragging.current = true;
    const rect = el.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.preventDefault();
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const el = pillRef.current;
      if (!el) return;
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = el.offsetWidth, h = el.offsetHeight;
      const newX = Math.max(0, Math.min(vw - w, e.clientX - dragOffset.current.x));
      const newY = Math.max(0, Math.min(vh - h, e.clientY - dragOffset.current.y));
      setPos({ x: vw - newX - w, y: vh - newY - h });
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  const handleStop = () => {
    push({ label: `Captured ${c.items.length} items`, category: 'workflow', detail: 'R2 Capture Mode' });
    c.toReview();
  };

  // 15% opacity when not hovered, full opacity on hover
  const baseOpacity = hovered ? 1 : 0.18;

  return (
    <div
      ref={pillRef}
      data-testid="capture-pill"
      style={{
        position: 'fixed', right: pos.x, bottom: pos.y, zIndex: 9570,
        opacity: baseOpacity,
        transition: 'opacity 0.25s ease, transform 0.2s ease',
        transform: hovered ? 'scale(1.02)' : 'scale(1)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Main pill */}
      <div style={{
        background: 'rgba(4,18,27,0.94)',
        border: '1px solid rgba(45,212,191,0.45)',
        borderRadius: expanded ? 12 : 999,
        boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(45,212,191,0.25)' : '0 4px 12px rgba(0,0,0,0.4)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        overflow: 'hidden',
        transition: 'border-radius 0.2s ease, box-shadow 0.2s ease',
      }}>
        {/* Pill header / drag handle */}
        <div
          onMouseDown={onMouseDown}
          style={{
            padding: expanded ? '8px 12px' : '8px 14px',
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'grab',
            minWidth: expanded ? 280 : 'auto',
          }}
        >
          {/* Pulsing mic dot */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: '#f43f5e',
              boxShadow: '0 0 0 0 rgba(244,63,94,0.4)',
              animation: 'recordPulse 1.4s ease infinite',
            }} />
          </div>

          <Mic size={12} style={{ color: '#5eead4', flexShrink: 0 }} />

          <span style={{ fontSize: 12, fontWeight: 700, color: '#e6fffb', whiteSpace: 'nowrap' }}>
            R2 Collecting
          </span>

          {/* Timer */}
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#5eead4', whiteSpace: 'nowrap' }}>
            {fmt(elapsed)}
          </span>

          {/* Item count badge */}
          {c.items.length > 0 && (
            <span style={{
              padding: '1px 7px', borderRadius: 999, fontSize: 10, fontWeight: 800,
              background: 'rgba(45,212,191,0.2)', color: '#5eead4', whiteSpace: 'nowrap',
            }}>
              {c.items.length}
            </span>
          )}

          <button onClick={() => setExpanded(v => !v)} style={{ background: 'none', border: 'none', color: 'rgba(94,234,212,0.5)', cursor: 'pointer', padding: 0, marginLeft: 4 }}>
            {expanded ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
          </button>
        </div>

        {/* Expanded quick-add area */}
        {expanded && <CollectMini onStop={handleStop} />}

        {/* Action strip */}
        {!expanded && (
          <div style={{
            display: 'flex', borderTop: '1px solid rgba(45,212,191,0.15)',
          }}>
            <button data-testid="capture-pill-pause" onClick={() => c.close()}
              style={{ flex: 1, padding: '6px 8px', background: 'none', border: 'none', color: 'rgba(148,172,210,0.55)', cursor: 'pointer', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', borderRight: '1px solid rgba(45,212,191,0.12)' }}>
              <Pause size={10} style={{ display: 'inline', marginRight: 3 }} /> PAUSE
            </button>
            <button data-testid="capture-pill-stop" onClick={handleStop}
              style={{ flex: 1, padding: '6px 8px', background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em' }}>
              ■ STOP
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes recordPulse {
          0%   { box-shadow: 0 0 0 0 rgba(244,63,94,0.5); }
          70%  { box-shadow: 0 0 0 8px rgba(244,63,94,0); }
          100% { box-shadow: 0 0 0 0 rgba(244,63,94,0); }
        }
      `}</style>
    </div>
  );
}

function CollectMini({ onStop }: { onStop: () => void }): JSX.Element {
  const c = useCapture();
  const [draft, setDraft] = useState('');

  const add = (type: CaptureType) => {
    if (!draft.trim()) return;
    c.addItem(type, draft, 'Quick add');
    setDraft('');
  };

  return (
    <div style={{ padding: '8px 12px 10px', borderTop: '1px solid rgba(45,212,191,0.15)' }}>
      <input
        data-testid="capture-mini-input"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') add('text'); }}
        placeholder="Quick add: paste text or link…"
        style={{
          width: '100%', padding: '6px 10px', borderRadius: 7,
          background: 'rgba(4,8,20,0.7)', border: '1px solid rgba(45,212,191,0.2)',
          color: '#e2e8f0', fontSize: 11, outline: 'none', boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: 6, marginTop: 7 }}>
        {(['text', 'note', 'link'] as CaptureType[]).map(t => (
          <button key={t} onClick={() => add(t)}
            style={{ flex: 1, padding: '5px', borderRadius: 5, background: 'rgba(45,212,191,0.08)', border: '1px solid rgba(45,212,191,0.2)', color: '#5eead4', fontSize: 9, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>
            + {t}
          </button>
        ))}
        <button data-testid="capture-pill-review" onClick={onStop}
          style={{ flex: 1, padding: '5px', borderRadius: 5, background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399', fontSize: 9, fontWeight: 800, cursor: 'pointer' }}>
          Review →
        </button>
      </div>
    </div>
  );
}

// ── Full Panel (config / review / ingested) ───────────────────────────────────
function CapturePanel(): JSX.Element {
  const c = useCapture();
  return (
    <div style={{
      position: 'fixed', right: 20, bottom: 20, zIndex: 9560,
      width: 'min(420px, calc(100vw - 40px))', maxHeight: '82vh',
      display: 'flex', flexDirection: 'column',
      borderRadius: 16, background: '#0b1220',
      border: '1px solid rgba(45,212,191,0.32)',
      boxShadow: '0 24px 70px rgba(0,0,0,0.6)',
    }} data-testid="r2-capture-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 16px', borderBottom: '1px solid rgba(45,212,191,0.16)' }}>
        <Sparkles style={{ width: 16, height: 16, color: '#5eead4' }} />
        <div style={{ fontSize: 13.5, fontWeight: 800, color: '#e6fffb' }}>R2 Capture Mode</div>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(148,163,184,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{c.stage}</span>
        <button type="button" data-testid="capture-close" data-nosound onClick={c.close} style={{ background: 'transparent', border: 'none', color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}>
          <X style={{ width: 16, height: 16 }} />
        </button>
      </div>
      <div style={{ overflowY: 'auto', padding: '16px' }}>
        {c.stage === 'config'    && <ConfigStage />}
        {c.stage === 'review'    && <ReviewStage />}
        {c.stage === 'ingested'  && <IngestedStage />}
      </div>
    </div>
  );
}

function ConfigStage(): JSX.Element {
  const c = useCapture();
  return (
    <div data-testid="capture-config">
      <p style={{ fontSize: 12.5, color: 'rgba(203,213,225,0.85)', marginBottom: 12 }}>Gather information from:</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {CAPTURE_TYPES.map((t) => (
          <label key={t.id} data-testid={`capture-type-${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, cursor: 'pointer',
            background: c.enabled[t.id] ? 'rgba(45,212,191,0.1)' : 'rgba(15,23,42,0.6)', border: `1px solid ${c.enabled[t.id] ? 'rgba(45,212,191,0.4)' : 'rgba(148,163,184,0.2)'}` }}>
            <input type="checkbox" checked={c.enabled[t.id]} onChange={() => c.toggleType(t.id)} className="accent-teal-400" />
            <span style={{ color: c.enabled[t.id] ? '#5eead4' : '#cbd5e1' }}>{ICONS[t.id]}</span>
            <span style={{ fontSize: 12.5, color: '#e2e8f0' }}>{t.label}</span>
          </label>
        ))}
      </div>
      <button type="button" data-testid="capture-start" onClick={c.startCollecting}
        style={{ width: '100%', marginTop: 14, padding: '11px', borderRadius: 10, fontWeight: 800, fontSize: 13, cursor: 'pointer', color: '#04121b', background: 'linear-gradient(135deg,#2dd4bf,#0d9488)', border: 'none' }}>
        Start Collecting → Minimizes to floating pill
      </button>
    </div>
  );
}

function ReviewStage(): JSX.Element {
  const c = useCapture();
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    c.items.forEach((i) => { m[i.type] = (m[i.type] ?? 0) + 1; });
    return m;
  }, [c.items]);
  const topics = useMemo(() => Array.from(new Set(c.items.flatMap((i) => i.keywords))).slice(0, 8), [c.items]);

  return (
    <div data-testid="capture-review">
      <p style={{ fontSize: 12.5, color: 'rgba(203,213,225,0.85)', marginBottom: 10, fontWeight: 700 }}>Collected Information</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {Object.entries(counts).map(([type, n]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', borderRadius: 8, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.16)' }}>
            <span style={{ color: '#5eead4' }}>{ICONS[type as CaptureType]}</span>
            <span style={{ fontSize: 12, color: '#e2e8f0', textTransform: 'capitalize', flex: 1 }}>{type}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#5eead4' }}>{n}</span>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '14px 0 6px' }}>Detected Topics</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="capture-topics">
        {topics.length ? topics.map((t) => (
          <span key={t} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, background: 'rgba(45,212,191,0.1)', border: '1px solid rgba(45,212,191,0.3)', color: '#5eead4' }}>{t}</span>
        )) : <span style={{ fontSize: 11.5, color: 'rgba(148,163,184,0.6)' }}>No topic keywords detected.</span>}
      </div>
      <p style={{ fontSize: 12.5, color: '#e2e8f0', margin: '16px 0 10px', fontWeight: 700 }}>Ready for ingestion?</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" data-testid="capture-approve" onClick={c.approve}
          style={{ flex: 1, padding: '10px', borderRadius: 10, fontWeight: 800, fontSize: 12.5, cursor: 'pointer', color: '#04121b', background: 'linear-gradient(135deg,#34d399,#10b981)', border: 'none' }}>
          YES — Ingest
        </button>
        <button type="button" data-testid="capture-back" onClick={c.backToCollecting}
          style={{ flex: 1, padding: '10px', borderRadius: 10, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', color: '#cbd5e1', background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}>
          NO — Review
        </button>
      </div>
    </div>
  );
}

function IngestedStage(): JSX.Element {
  const c = useCapture();
  const r = c.report;
  const icon = (sev: string) => sev === 'error' ? <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#f87171' }} /> : sev === 'warn' ? <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#fbbf24' }} /> : <CheckCircle2 className="h-3.5 w-3.5" style={{ color: '#34d399' }} />;
  return (
    <div data-testid="capture-ingested">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 9, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', marginBottom: 12 }}>
        <ClipboardCheck style={{ width: 16, height: 16, color: '#34d399' }} />
        <span style={{ fontSize: 12.5, color: '#a7f3d0' }}>Ingested {r?.itemCount ?? 0} items → Knowledge Graph → QA/QC</span>
      </div>
      <p style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>QA/QC Reference Intelligence Map</p>
      <div data-testid="qaqc-findings">
        {r?.findings.map((f, i) => (
          <div key={i} style={{ padding: '9px 11px', borderRadius: 9, background: 'rgba(15,23,42,0.6)', border: `1px solid ${f.severity === 'error' ? 'rgba(248,113,113,0.3)' : f.severity === 'warn' ? 'rgba(251,191,36,0.3)' : 'rgba(52,211,153,0.25)'}`, marginBottom: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>{icon(f.severity)}<span style={{ fontSize: 12.5, fontWeight: 700, color: '#e2e8f0' }}>{f.term}</span></div>
            <div style={{ fontSize: 11.5, color: 'rgba(203,213,225,0.85)', marginTop: 4, lineHeight: 1.5 }}>{f.message}</div>
            {f.missing && f.missing.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 4, listStyle: 'none' }}>
                {f.missing.map((m) => <li key={m} style={{ fontSize: 11, color: '#f87171' }}>✗ Missing: {m}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
      <button type="button" data-testid="capture-reset" onClick={c.reset}
        style={{ width: '100%', marginTop: 8, padding: '9px', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer', color: '#cbd5e1', background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}>
        Start a New Capture
      </button>
    </div>
  );
}
